import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.jsx'
import { usePeptides } from '../context/PeptidesContext.jsx'
import CalculatorForm from '../components/CalculatorForm.jsx'
import CalculatorBanner from '../components/CalculatorBanner.jsx'
import CalcGuide from '../components/CalcGuide.jsx'
import { WELLNESS_CALCS } from '../data/wellness.js'
import Seo from '../components/Seo.jsx'

export default function Home() {
  const { user } = useAuth()
  const { peptides } = usePeptides()
  const popular = peptides.slice(0, 6)

  return (
    <main className="page">
      <Seo
        title="Peptide Reconstitution Calculator | CalculateMyPeptides"
        description="Free peptide reconstitution and dosing calculator. Get exact insulin-syringe units, concentration, and doses per vial, plus a peptide education library and wellness calculators."
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'CalculateMyPeptides',
            url: 'https://calculatemypeptides.com/',
            applicationCategory: 'HealthApplication',
            operatingSystem: 'Web',
            description: 'Free peptide reconstitution and dosing calculator: enter vial amount, bacteriostatic water, and target dose to get the exact insulin-syringe units to draw, concentration, and doses per vial.',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'CalculateMyPeptides',
            url: 'https://calculatemypeptides.com/',
            logo: 'https://calculatemypeptides.com/favicon.svg',
            description: 'Free peptide reconstitution & dosing calculator, an educational peptide library, and health & wellness calculators.',
          },
        ]}
      />
      <div className="container">
        {/* sponsor / ad slot (renders nothing until banners are added) */}
        <CalculatorBanner />

        {/* hero + live calculator */}
        <section className="home-hero">
          <span className="pill blue" style={{ marginBottom: 12 }}>Free · Accurate · Private</span>
          <h1>
            Peptide <span className="grad">Reconstitution &amp; Dosage</span> Calculator
          </h1>
          <p className="lead">
            Enter your vial, bacteriostatic water, and target dose to get the exact number of insulin-syringe
            units to draw — plus a plain-English breakdown, a live syringe, concentration, and doses per vial.
            Save your protocols and schedule every injection.
          </p>
        </section>

        <CalculatorForm />
        <p className="muted small" style={{ textAlign: 'center', margin: '10px 0 0' }}>
          Educational math tool only — not medical advice. Follow a qualified healthcare professional’s guidance.
        </p>

        {/* features */}
        <section className="feature-grid">
          <Feature icon="🧮" title="Exact reconstitution">
            Concentration, draw volume, and insulin units instantly — with a plain-English “here’s what to do”
            and warnings when a dose won’t fit the syringe.
          </Feature>
          <Feature icon="💾" title="Saved protocols">
            Store every calculation under your free account and reload it in one tap — no re-typing the same
            math each week.
          </Feature>
          <Feature icon="🗓️" title="Dosing calendar">
            Schedule doses across days or weeks and check off each injection as you take it, so you always know
            what’s next.
          </Feature>
        </section>

        {/* SEO / educational content */}
        <section className="home-seo">
          <h2 className="section-title">How peptide reconstitution works</h2>
          <p>
            Reconstitution is simply mixing a powdered (lyophilized) peptide with bacteriostatic water so it can
            be drawn and measured accurately. The amount of water you add sets the <strong>concentration</strong>
            {' '}— more water means a weaker solution and a larger, easier-to-read volume per dose; less water
            means a stronger solution and fewer units to draw.
          </p>
          <p>
            Our <Link to="/calculator">calculator</Link> converts your target dose into exact insulin-syringe
            units and shows how many doses a vial holds — and our{' '}
            <Link to="/peptide-dosage-guide">peptide dosage guide</Link> walks through the whole process. If
            you’re new to a compound, start with our{' '}
            <Link to="/peptides">peptide education library</Link> for plain-language profiles, and use the{' '}
            <Link to="/wellness">wellness calculators</Link> for everyday metrics like BMI, BMR, and daily
            calories. Everything here is educational and not medical advice.
          </p>
        </section>

        {/* step-by-step guide + worked example (shared with /calculator) */}
        <section className="home-seo">
          <CalcGuide />
        </section>

        {/* explore / internal links */}
        <section className="home-explore">
          <div className="explore-col">
            <h3>Popular peptides</h3>
            <ul className="explore-list">
              {popular.map((p) => (
                <li key={p.slug}><Link to={`/peptides/${p.slug}`}>{p.name}</Link> <span className="muted small">— {p.category}</span></li>
              ))}
            </ul>
            <Link to="/peptides" className="btn btn-ghost btn-sm">Browse all →</Link>
          </div>
          <div className="explore-col">
            <h3>Wellness calculators</h3>
            <ul className="explore-list">
              {WELLNESS_CALCS.map((c) => (
                <li key={c.slug}><Link to={`/wellness/${c.slug}`}>{c.name}</Link></li>
              ))}
            </ul>
            <Link to="/wellness" className="btn btn-ghost btn-sm">See all →</Link>
          </div>
        </section>

        {!user && (
          <section className="home-cta card card-pad">
            <div>
              <h2 style={{ marginBottom: 4 }}>Save your protocols &amp; build a schedule</h2>
              <p className="muted mb-0">Create a free account to store calculations and track every dose.</p>
            </div>
            <Link to="/register" className="btn btn-primary">Create free account</Link>
          </section>
        )}
      </div>
    </main>
  )
}

function Feature({ icon, title, children }) {
  return (
    <div className="card feature">
      <div className="fi">{icon}</div>
      <h2>{title}</h2>
      <p>{children}</p>
    </div>
  )
}
